import { Map, Set } from 'immutable';

import { getDefaultFilters, getDefaultStatuses, shapeFiltersForAPI, shapeFiltersForApp } from '../utils/profiles/connectProfile';
import { getDefaultPagination, getPagination, getPaginationForAPI } from '../utils/listings/pagination';
import { shapeId, shapeListingsForApp, shapeNumber } from '../utils/listings/listings';
import { getFirstNonEmptyStatus, shapeCountsForApp } from '../utils/listings/statuses';
import { isFeatureEnabled } from '../utils/featureFlags';
import { createController } from '../utils/controller';
import { getOrderForAPI } from '../utils/listings/order';
import { setSelection } from '../utils/listings/selection';
import { increase } from '../utils/math';
import { getSize } from '../utils/iterable/getSize';
import api from '../utils/api';

import { ALPHABETICAL_ORDER, FILTER, PAGE_SIZE, SELECTION } from '../constants/listings';
import { MESSAGE, NOTIFICATION } from '../constants/notifications';
import { FEATURES } from '../constants/billing';
import { EMPTY } from '../constants';

import actions from '../actions';

const filtersController = createController();
const listingsController = createController();
const listingIdsController = createController();
const statusesController = createController();

function abort(controller) {
  if (controller.isActive()) {
    controller.stop();
  }
}

function abortAll() {
  [
    filtersController,
    listingsController,
    listingIdsController,
    statusesController,
  ].forEach(abort);
}

function* bootstrap(reduction, { channel, db, profileId, shopId }) {
  abortAll();

  const state = Map({
    channel,
    db,
    filters: getDefaultFilters(),
    pagination: getDefaultPagination(),
    products: EMPTY.MAP,
    profileId,
    shopId,
    statuses: getDefaultStatuses(),
  });

  yield function effect(dispatch) {
    dispatch(actions.connectProfile.getStatuses());
  };

  return reduction.set('connectProfile', state);
}

function* cleanUp(reduction) {
  abortAll();
  return reduction.delete('connectProfile');
}

function* getFilters(reduction, { filters: selected } = {}) {
  abort(filtersController);

  yield function effect(dispatch) {
    const { signal } = filtersController.start();
    const userId = reduction.getIn(['user', 'userId']);
    const state = reduction.get('connectProfile');
    const channel = state.get('channel');
    const db = state.get('db');
    const shopId = state.get('shopId');
    const status = state.getIn(['statuses', 'selected']);
    const filters = shapeFiltersForAPI({ filters: selected || state.getIn(['filters', 'selected']), status, userId });

    api.listings
      .getFilters({ channel, db, filters, shopId, signal })
      .then(
        (response) => dispatch(actions.connectProfile.getFilters.fulfilled({ response, selected })),
        (error) => dispatch(actions.connectProfile.getFilters.rejected({ error, signal })),
      );
  };

  return reduction.setIn(['connectProfile', 'filters', 'loading'], true);
}

function* getFiltersFulfilled(reduction, { response, selected }) {
  if (!reduction.has('connectProfile')) return reduction;

  abort(filtersController);

  const state = reduction.get('connectProfile');
  const channel = state.get('channel');

  if (!channel) return reduction;

  let filters = state.get('filters').delete('loading');

  if (selected) {
    filters = filters.set('selected', selected);
  }

  filters = shapeFiltersForApp({
    channel,
    filters,
    response,
    shopData: reduction.getIn(['data', 'shopsData', state.get('shopId')]),
    status: state.getIn(['statuses', 'selected']),
    userId: reduction.getIn(['user', 'userId']),
  });

  return reduction.setIn(['connectProfile', 'filters'], filters);
}

function* getFiltersRejected(reduction, { error, signal }) {
  if (signal.aborted || !reduction.has('connectProfile')) return reduction;

  abort(filtersController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return reduction.deleteIn(['connectProfile', 'filters', 'loading']);
}

function* getListings(reduction, payload = {}) {
  abort(listingsController);

  const { clear, filters: selected } = payload;

  if (clear) {
    abort(listingIdsController);
  }

  yield function effect(dispatch) {
    const { signal } = listingsController.start();
    const userId = reduction.getIn(['user', 'userId']);
    const state = reduction.get('connectProfile');
    const channel = state.get('channel');
    const db = state.get('db');
    const shopId = state.get('shopId');
    const status = state.getIn(['statuses', 'selected']);
    const page = clear
      ? 0
      : state.getIn(['pagination', 'currentPage']);

    const filters = {
      ...shapeFiltersForAPI({ filters: selected || state.getIn(['filters', 'selected']), status, userId }),
      ...getOrderForAPI({ channel, order: ALPHABETICAL_ORDER[channel], status }),
      ...getPaginationForAPI(page),
    };

    api.listings
      .getListings({ channel, db, filters, shopId, signal })
      .then(
        (response) => dispatch(
          actions.connectProfile.getListings.fulfilled({ clear, page, response, status })
        ),
        (error) => dispatch(actions.connectProfile.getListings.rejected({ error, signal })),
      );
  };

  return reduction.setIn(['connectProfile', 'products', 'loading'], true);
}

function* getListingsFulfilled(reduction, { clear, page, response, status }) {
  if (!reduction.has('connectProfile')) return reduction;

  abort(listingsController);

  let state = reduction.get('connectProfile');
  const channel = state.get('channel');

  if (!channel) return reduction;

  state = state.deleteIn(['products', 'loading']);

  if (clear) {
    state = state
      .deleteIn(['products', 'all'])
      .deleteIn(['products', 'selection'])
      .setIn(['products', 'selected'], EMPTY.ORDERED_SET);
  }

  if (status !== state.get('status')) {
    state = state.set('status', status);
  }

  const total = shapeNumber(response.total);

  state = state.setIn(['products', 'total'], total);

  if (!total) {
    state = state
      .deleteIn(['products', 'all'])
      .deleteIn(['products', 'byId'])
      .deleteIn(['products', 'ids'])
      .deleteIn(['products', 'selection'])
      .setIn(['products', 'selected'], EMPTY.ORDERED_SET)
      .set('pagination', getDefaultPagination());

    return reduction.set('connectProfile', state);
  } else if (!getSize(response.listings) && page > 0) {
    const lastPage = Math.floor((total - 1) / PAGE_SIZE);

    if (state.getIn(['pagination', 'currentPage']) > lastPage) {
      state = state.set('pagination', getPagination({ page: lastPage, total }));
    }

    return reduction.set('connectProfile', state);
  }

  const shopId = state.get('shopId');
  const userShops = reduction.get('shops');
  const shopData = reduction.getIn(['data', 'shopsData', shopId]);
  const { byId, ids } = shapeListingsForApp({ channel, response, shopData, userShops });
  let products = page > 0
    ? state.get('products').mergeIn(['byId'], byId).mergeIn(['ids'], ids)
    : state.get('products').set('byId', byId).set('ids', ids);

  const connected = products.get('connected', EMPTY.SET);

  function filterConnected(id) {
    return !connected.has(id);
  }

  if (!products.has('all') && getSize(products.get('ids')) === products.get('total')) {
    products = products.set('all', products.get('ids').filter(filterConnected));
  }

  state = state
    .set('pagination', getPagination({ page, total }))
    .set('products', setSelection({ exclude: connected, products }));

  return reduction.set('connectProfile', state);
}

function* getListingsRejected(reduction, { error, signal }) {
  if (signal.aborted || !reduction.has('connectProfile')) return reduction;

  abort(listingsController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_LISTINGS,
      })
    );
  };

  return reduction.deleteIn(['connectProfile', 'products', 'loading']);
}

function* getStatuses(reduction) {
  abort(statusesController);

  yield function effect(dispatch) {
    const { signal } = statusesController.start();
    const state = reduction.get('connectProfile');
    const channel = state.get('channel');
    const db = state.get('db');
    const shopId = state.get('shopId');

    api.listings
      .getStatuses({ channel, db, shopId, signal })
      .then(
        (response) => dispatch(actions.connectProfile.getStatuses.fulfilled(response)),
        (error) => dispatch(actions.connectProfile.getStatuses.rejected({ error, signal })),
      );
  };

  return reduction;
}

function* getStatusesFulfilled(reduction, response) {
  if (!reduction.has('connectProfile')) return reduction;

  abort(statusesController);

  let state = reduction.get('connectProfile');
  const channel = state.get('channel');

  if (!channel) return reduction;

  const counts = shapeCountsForApp({ channel, response });
  let statuses = state.get('statuses').set('counts', counts);

  function getEmptyState() {
    return state
      .set('filters', getDefaultFilters().set('options', EMPTY.LIST))
      .set('pagination', getDefaultPagination())
      .set('products', Map({ total: 0 }));
  }

  if (!statuses.getIn(['counts', statuses.get('selected')])) {
    const firstNonEmpty = getFirstNonEmptyStatus({ channel, counts });

    if (firstNonEmpty) {
      statuses = statuses.set('selected', firstNonEmpty);
    } else {
      state = getEmptyState().set('status', statuses.get('selected'));
    }
  }

  yield function effect(dispatch) {
    const filters = EMPTY.MAP;
    dispatch(actions.connectProfile.getFilters({ filters }));
    dispatch(actions.connectProfile.getListings({ clear: true, filters }));
  };

  return reduction.set('connectProfile', state.set('statuses', statuses));
}

function* getStatusesRejected(reduction, { error, signal }) {
  if (signal.aborted || !reduction.has('connectProfile')) return reduction;

  abort(statusesController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return reduction;
}

function* setConnectedProducts(reduction, products) {
  if (!reduction.has('connectProfile')) return reduction;

  return reduction.setIn(['connectProfile', 'products', 'connected'], Set(products));
}

function* setFilter(reduction, { filter, value, ...rest }) {
  let filters = reduction.getIn(['connectProfile', 'filters', 'selected']);

  switch (filter) {
    case FILTER.PROFILE: {
      if (isFeatureEnabled({ feature: FEATURES.PROFILES_V2, userId: reduction.getIn(['user', 'userId']) })) {
        filters = filters.update(filter, function updateFilter(values = EMPTY.SET) {
          return values.has(value)
            ? values.delete(value)
            : values.add(value);
        });
      } else {
        filters = filters.update(filter, function updateFilter(types = EMPTY.MAP) {
          const { type } = rest;

          let ids = types.get(type, EMPTY.SET);

          ids = ids.has(value)
            ? ids.delete(value)
            : ids.add(value);

          return getSize(ids)
            ? types.set(type, ids)
            : types.delete(type);
        });
      }

      break;
    }

    case FILTER.TITLE: {
      filters = filters.set(filter, value);
      break;
    }

    default: {
      filters = filters.update(filter, function updateFilter(values = EMPTY.SET) {
        return Set.isSet(value)
          ? getSize(values.intersect(value))
            ? values.subtract(value)
            : values.union(value)
          : values.has(value)
            ? values.delete(value)
            : values.add(value);
      });

      break;
    }
  }

  if (!getSize(filters.get(filter))) {
    filters = filters.delete(filter);
  }

  yield function effect(dispatch) {
    dispatch(actions.connectProfile.getFilters({ filters }));
    dispatch(actions.connectProfile.getListings({ clear: true, filters }));
  };

  return reduction.setIn(['connectProfile', 'filters', 'selected'], filters);
}

function* loadMore(reduction) {
  if (!reduction.has('connectProfile')) return reduction;

  const pagination = reduction.getIn(['connectProfile', 'pagination']);

  if (pagination.get('to') >= pagination.get('total')) return reduction;

  yield function effect(dispatch) {
    dispatch(actions.connectProfile.getListings());
  };

  return reduction.updateIn(['connectProfile', 'pagination', 'currentPage'], increase);
}

function* setStatus(reduction, status) {
  abort(filtersController);
  abort(listingsController);
  abort(listingIdsController);

  let state = reduction.get('connectProfile');
  const statuses = state.get('statuses');

  if (status === statuses.get('selected')) return reduction;

  if (!statuses.getIn(['counts', status])) {
    state = state
      .set('filters', getDefaultFilters().set('options', EMPTY.LIST))
      .set('pagination', getDefaultPagination())
      .set('products', Map({ total: 0 }))
      .set('status', status)
      .set('statuses', statuses.set('selected', status));

    return reduction.set('connectProfile', state);
  }

  let filters = EMPTY.MAP;
  const title = state.getIn(['filters', 'selected', 'title']);

  if (title) {
    filters = filters.set('title', title);
  }

  yield function effect(dispatch) {
    dispatch(actions.connectProfile.getFilters({ filters }));
    dispatch(actions.connectProfile.getListings({ clear: true, filters }));
  };

  return reduction.setIn(['connectProfile', 'statuses'], statuses.set('selected', status));
}

function* toggleProduct(reduction, productId) {
  abort(listingIdsController);
  let products = reduction.getIn(['connectProfile', 'products']);
  const connected = products.get('connected', EMPTY.SET);

  if (productId) {
    products = products.update('selected', function update(selected = EMPTY.ORDERED_SET) {
      return selected.has(productId)
        ? selected.delete(productId)
        : selected.add(productId);
    });
  }

  return reduction.setIn(['connectProfile', 'products'], setSelection({ exclude: connected, products }));
}

function* toggleAllProducts(reduction, limit) {
  const state = reduction.get('connectProfile');
  const products = state.get('products');

  function reduceIds(result, id) {
    return getSize(result) < limit && !products.hasIn(['connected', id]) && products.hasIn(['selected', id])
      ? result.add(id)
      : result;
  }

  if (limit) {
    if (products.has('all')) {
      return reduction.setIn(['connectProfile', 'products'],
        setSelection({ products: products.set('selected', products.get('all').slice(0, limit).toOrderedSet()) })
      );
    } else if (limit <= getSize(products.get('ids'))) {
      return reduction.setIn(['connectProfile', 'products'],
        setSelection({ products: products.set('selected', products.get('ids').reduce(reduceIds, EMPTY.ORDERED_SET)) })
      );
    }
  } else if (products.get('selection') === SELECTION.ALL) {
    return reduction.setIn(['connectProfile', 'products'],
      setSelection({ products: products.set('selected', EMPTY.ORDERED_SET) })
    );
  } else if (products.has('all')) {
    return reduction.setIn(['connectProfile', 'products'],
      setSelection({ products: products.set('selected', products.get('all').toOrderedSet()) })
    );
  }

  yield function effect(dispatch) {
    const { signal } = listingIdsController.start();
    const userId = reduction.getIn(['user', 'userId']);
    const channel = state.get('channel');
    const db = state.get('db');
    const shopId = state.get('shopId');
    const filters = state.getIn(['filters', 'selected']);
    const status = state.getIn(['statuses', 'selected']);
    const payload = {
      ...shapeFiltersForAPI({ filters, status, userId }),
      ...getOrderForAPI({ channel, order: ALPHABETICAL_ORDER[channel], status }),
    };

    api.listings
      .getListingIds({ channel, db, payload, shopId, signal })
      .then(
        (response) => dispatch(actions.connectProfile.toggleAllProducts.fulfilled({ limit, response })),
        (error) => dispatch(actions.connectProfile.toggleAllProducts.rejected({ error, signal })),
      );
  };

  return reduction.setIn(['connectProfile', 'products', 'loading'], true);
}

function* toggleAllProductsFulfilled(reduction, { limit, response }) {
  if (!reduction.has('connectProfile')) return reduction;

  abort(listingIdsController);

  const products = reduction.getIn(['connectProfile', 'products']).delete('loading');
  const connected = products.get('connected', EMPTY.SET);

  function reduceIds(result, id) {
    return connected.has(id)
      ? result
      : result.push(shapeId(id));
  }

  const all = response.reduce(reduceIds, EMPTY.LIST);
  const selected = limit ? all.slice(0, limit).toOrderedSet() : all.toOrderedSet();

  return reduction.setIn(['connectProfile', 'products'],
    setSelection({
      exclude: connected,
      products: products.set('all', all).set('selected', selected),
    })
  );
}

function* toggleAllProductsRejected(reduction, { error, signal }) {
  if (signal.aborted || !reduction.has('connectProfile')) return reduction;

  abort(listingIdsController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_DATA,
      })
    );
  };

  return reduction.deleteIn(['connectProfile', 'products', 'loading']);
}

const connectProfile = {
  [actions.connectProfile.bootstrap]: bootstrap,
  [actions.connectProfile.cleanUp]: cleanUp,
  [actions.connectProfile.getFilters]: getFilters,
  [actions.connectProfile.getFilters.fulfilled]: getFiltersFulfilled,
  [actions.connectProfile.getFilters.rejected]: getFiltersRejected,
  [actions.connectProfile.getListings]: getListings,
  [actions.connectProfile.getListings.fulfilled]: getListingsFulfilled,
  [actions.connectProfile.getListings.rejected]: getListingsRejected,
  [actions.connectProfile.getStatuses]: getStatuses,
  [actions.connectProfile.getStatuses.fulfilled]: getStatusesFulfilled,
  [actions.connectProfile.getStatuses.rejected]: getStatusesRejected,
  [actions.connectProfile.loadMore]: loadMore,
  [actions.connectProfile.setConnectedProducts]: setConnectedProducts,
  [actions.connectProfile.setFilter]: setFilter,
  [actions.connectProfile.setStatus]: setStatus,
  [actions.connectProfile.toggleProduct]: toggleProduct,
  [actions.connectProfile.toggleAllProducts]: toggleAllProducts,
  [actions.connectProfile.toggleAllProducts.fulfilled]: toggleAllProductsFulfilled,
  [actions.connectProfile.toggleAllProducts.rejected]: toggleAllProductsRejected,
};

export default connectProfile;
